import {
  Button,
  Dialog,
  Input,
  OverlayArrow,
  Popover,
  Tooltip,
  TooltipTrigger,
} from "react-aria-components";
import { ArrowsRightLeftIcon } from "@heroicons/react/24/solid";
import type { Tenant } from "~/gen/efibi/v1/hub/common/tenant_pb";
import { useFetcher } from "react-router";
import { IndeterminateSpinner } from "~/ui/IndeterminateSpinner";
import { useEffect, useRef, useState } from "react";
import { stringify } from "uuid";

export interface TenantSwitcherProps {
  tenantLogo: string;
  tenant: Tenant | null;
}

function extractTenantId(tenant: Tenant | null): string | null {
  if (tenant == null) {
    return null;
  }
  return stringify(tenant.id);
}

export default function TenantSwitcher({
  tenantLogo,
  tenant,
}: TenantSwitcherProps) {
  const [isOpen, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const [previousTenant, setPreviousTenant] = useState(tenant);
  useEffect(() => {
    const previousTenantId = extractTenantId(previousTenant);
    const currentTenantId = extractTenantId(tenant);
    if (previousTenantId === currentTenantId) {
      return;
    }
    setPreviousTenant(tenant);
    if (previousTenantId == null || currentTenantId != null) {
      setOpen(false);
    }
  }, [previousTenant, tenant]);

  return (
    <div className="flex flex-row items-center w-full rounded-md border-0 p-2 mt-10 shadow-sm ring-1 ring-inset ring-gray-300">
      <img
        className="h-16 w-16"
        src={tenantLogo}
        alt={`Logo ${tenant?.name ?? ""}`}
      />
      <div className="flex-1 px-4">
        <h3 className="font-semibold">{tenant?.name ?? ""}</h3>
        <h4 className="text-sm">{tenant?.shortName ?? ""}</h4>
      </div>
      <div className="h-16 w-16">
        <TooltipTrigger>
          <Button
            ref={triggerRef}
            className="h-16 w-16 rounded-md hover:bg-gray-100 text-center"
            type="button"
            onPress={() => setOpen(!isOpen)}
          >
            <ArrowsRightLeftIcon className="inline h-8 w-8" />
          </Button>
          <Tooltip className="bg-gray-800 text-white text-sm rounded p-1.5">
            <OverlayArrow>
              <svg
                width={8}
                height={8}
                viewBox="0 0 8 8"
                className="text-gray-800"
              >
                <path fill="currentColor" d="M0 0 L4 4 L8 0" />
              </svg>
            </OverlayArrow>
            Changer de domaine
          </Tooltip>
        </TooltipTrigger>
        <Popover
          isOpen={isOpen}
          triggerRef={triggerRef}
          onOpenChange={setOpen}
          placement="end"
          offset={24}
        >
          <Dialog>
            <SwitcherForm />
          </Dialog>
        </Popover>
      </div>
    </div>
  );
}

function SwitcherForm() {
  const switcher = useFetcher();
  const isLoading = switcher.state !== "idle";

  return (
    <switcher.Form
      className="flex flex-row p-4 bg-white rounded shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
      method="post"
      action="/auth/tenant-nudge"
    >
      <div className="flex rounded-md shadow-sm">
        <div className="relative flex flex-grow items-stretch focus-within:z-10">
          <Input
            className="block w-full rounded-none rounded-l-md border-0 p-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            name="tenantSlug"
            type="text"
            required={true}
            autoFocus={true}
            autoComplete="organization"
            disabled={isLoading}
          />
        </div>
        <Button
          className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          isDisabled={isLoading}
          type="submit"
        >
          <span className="block w-20 text-center">
            {!isLoading && "Valider"}
            {isLoading && (
              <IndeterminateSpinner className="h-6 w-6 block mx-auto" />
            )}
          </span>
        </Button>
      </div>
    </switcher.Form>
  );
}
